import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, Flex, Spinner, useColorModeValue, Tooltip, Box } from '@vimeo/bokeh';
import {
  ArrowUpRightPopOut,
  Checkmark,
  ChevronRight,
  CloseX,
  Envelope,
  InfoCircle,
  Link,
  ListUl,
  Play as PlayIcon,
  Plus,
  PopOut,
  QuestionCircle as QuestionIcon,
  Reset,
  ScissorsEditTrim,
  TranslateFilled,
  Video,
  SceneMedia,
} from '@vimeo/bokeh-icons';
import { shouldShowInDevelopmentFeature } from '@vimeo/query-feature-flag';

import { useGetSvvManageBpEvents } from '../../bp-events';
import { AI_PRIMARY_BUTTON_COLORS } from '../../constants';
import * as draftCopy from '../../copy/draft';
import * as finalCopy from '../../copy/final';
import type { IInheritColors } from '../../stores/viewer-ui-store';
import { useUIStore } from '../../stores/viewer-ui-store';
import { getChoiceButtonSx, WrappingButtonSx } from './Choice.styles';

interface Props {
  children: ReactNode;
  isDisabled?: boolean;
  isPrimary?: boolean;
  hasUpsell?: boolean;
  isLoading?: boolean;
  shouldWrap?: boolean;
  title?: string;
  text?: string;
  inheritColors?: IInheritColors | null;
  dataId?: string;
  /**
   * Should be a string value that maps to an existing Bokeh icon.
   * See the getIconFromUnknown method in this component file.
   * If a non-string value is passed, we return null.
   * If a string that doesn't recogniz∂e is passed, we default to <CircleInfo />
   */
  icon?: unknown;
  href?: string;
  onClick?: VoidFunction;
  /** Indicates if this button is in the root menu of the AI Action Menu */
  isRootMenuButton?: boolean;
}

const ChoiceDynamicText = ({ text }: { text: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  useEffect(
    function handleEllipsisCheck() {
      const checkEllipsis = () => {
        if (textRef.current) {
          setIsEllipsis(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
      };

      checkEllipsis();
      window.addEventListener('resize', checkEllipsis);
      return () => window.removeEventListener('resize', checkEllipsis);
    },
    [text],
  );

  return (
    <Box as={'span'} overflow={'hidden'} ref={textRef}>
      {isEllipsis ? (
        <Tooltip label={text} placement={'top'}>
          <Box
            as={'span'}
            flex={'1 1 auto'}
            minW={0}
            overflow={'hidden'}
            whiteSpace={'nowrap'}
            textOverflow={'ellipsis'}
            display={'block'}
            textAlign={'left'}
            fontSize={'heading-xs'}
          >
            {text}
          </Box>
        </Tooltip>
      ) : (
        text
      )}
    </Box>
  );
};

const UpsellButton = ({ text }: { text: string }) => (
  <Button variant="upsell" size="xs" marginLeft={8}>
    {text}
  </Button>
);
const TextButtonWrapper = ({
  isLoading,
  onClick,
  isPrimary,
  isDisabled,
  icon,
  choiceText,
  hasUpsell,
  isRootMenuButton,
}: {
  isLoading?: boolean;
  onClick?: () => void;
  isPrimary?: boolean;
  isDisabled?: boolean;
  icon: string;
  choiceText: string;
  hasUpsell?: boolean;
  isRootMenuButton?: boolean;
}) => {
  return (
    <Choice
      icon={icon}
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClick}
      isLoading={isLoading}
      isRootMenuButton={isRootMenuButton}
      hasUpsell={hasUpsell}
    >
      <Flex
        sx={{
          position: 'relative',
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden',
          justifyContent: 'space-between',
        }}
      >
        <ChoiceDynamicText text={choiceText} />
        {hasUpsell && <UpsellButton text={finalCopy.upgrade} />}
      </Flex>
    </Choice>
  );
};

function getIconFromUnknown(icon?: unknown) {
  if (typeof icon === 'string') {
    switch (icon) {
      case 'info':
        return <InfoCircle />;
      case 'chapters':
        return <ListUl />;
      case 'video':
        return <Video />;
      case 'scene-media':
        return <SceneMedia />;
      case 'check':
        return <Checkmark />;
      case 'plus':
        return <Plus />;
      case 'question':
        return <QuestionIcon />;
      case 'play':
        return <PlayIcon />;
      case 'email':
        return <Envelope />;
      case 'popOut':
        return <PopOut />;
      case 'scissors':
        return <ScissorsEditTrim />;
      case 'link':
        return <Link />;
      case 'cancel':
        return <CloseX />;
      case 'arrow-out':
        return <ArrowUpRightPopOut />;
      case 'reset':
        return <Reset />;
      case 'translations':
        return <TranslateFilled />;
      case 'empty':
        return <></>;
      default:
        return <InfoCircle />;
    }
  }
  return <></>;
}

/**
 * Basically just a button element with an optional icon.
 * This is the Base component.
 */
export function Choice({
  href,
  children,
  isDisabled = false,
  isPrimary = false,
  icon,
  onClick,
  isLoading = false,
  shouldWrap = false,
  inheritColors = null,
  dataId,
  isRootMenuButton,
  hasUpsell = false,
}: Props) {
  const iconElement = getIconFromUnknown(icon);

  const linkProps = href
    ? {
        // needs to be done for chakra button prop type
        // couldn't find the right export path for the 'As' type
        as: 'a' as 'a',
        href,
        target: '_blank',
      }
    : {};

  const primaryButtonColors: {
    normal: string;
    hover: string;
    disabled: string;
    textColor: string;
  } = useColorModeValue(AI_PRIMARY_BUTTON_COLORS.light, AI_PRIMARY_BUTTON_COLORS.dark);

  const primaryStyles = isPrimary
    ? {
        background: inheritColors?.primary || primaryButtonColors.normal,
        _disabled: {
          background: primaryButtonColors.disabled,
          _hover: { background: primaryButtonColors.disabled, cursor: 'default' },
        },
        _hover: {
          background: inheritColors?.accentHover || primaryButtonColors.hover,
          ...(inheritColors?.textHoverColor ? { color: inheritColors.textHoverColor } : {}),
        },
        color: inheritColors?.iconTextColor || primaryButtonColors.textColor,
      }
    : {};

  return (
    <Flex w="100%" position={'relative'}>
      <Button
        isDisabled={isDisabled}
        variant={isRootMenuButton ? 'minimal' : 'secondary'}
        leftIcon={iconElement}
        onClick={onClick}
        size={!isRootMenuButton ? 'md' : 'sm'}
        sx={{
          ...(shouldWrap ? WrappingButtonSx : getChoiceButtonSx(isRootMenuButton)),
          ...(isRootMenuButton ? { 'data-root-menu-button': 'true' } : {}),
        }}
        {...primaryStyles}
        {...linkProps}
        {...(dataId ? { 'data-testid': dataId } : {})}
      >
        {children}
      </Button>
      {!isLoading && isRootMenuButton && !hasUpsell && (
        <ChevronRight
          boxSize={'2xs'}
          sx={{
            position: 'absolute !important',
            right: '1rem !important',
            top: '0 !important',
            bottom: '0 !important',
            alignSelf: 'center !important',
          }}
        />
      )}
      {isLoading ? (
        <Spinner
          size="sm"
          sx={{
            position: 'absolute !important',
            right: '1rem !important',
            top: '0 !important',
            bottom: '0 !important',
            alignSelf: 'center !important',
          }}
        />
      ) : null}
    </Flex>
  );
}

const Summary = ({
  isDisabled,
  isPrimary,
  onClick,
  isLoading,
  hasUpsell,
  isRootMenuButton,
}: Pick<
  Props,
  'isDisabled' | 'isPrimary' | 'onClick' | 'isLoading' | 'hasUpsell' | 'isRootMenuButton'
>) => {
  const { sendEnterpriseCTAImpressionEvent } = useGetSvvManageBpEvents();
  useEffect(
    function handleUpsellStatus() {
      if (hasUpsell) {
        sendEnterpriseCTAImpressionEvent('video_details');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpsell],
  );

  return (
    <TextButtonWrapper
      icon="info"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClick}
      isLoading={isLoading}
      choiceText={finalCopy.generateVideoSummary}
      hasUpsell={hasUpsell}
      isRootMenuButton={isRootMenuButton}
    />
  );
};

const Chapters = ({
  isDisabled,
  isPrimary,
  onClick,
  isLoading,
  hasUpsell,
  isRootMenuButton,
}: Pick<
  Props,
  'isDisabled' | 'isPrimary' | 'onClick' | 'isLoading' | 'hasUpsell' | 'isRootMenuButton'
>) => {
  const { sendEnterpriseCTAImpressionEvent } = useGetSvvManageBpEvents();
  useEffect(
    function handleUpsellStatus() {
      if (hasUpsell) {
        sendEnterpriseCTAImpressionEvent('chapters');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpsell],
  );
  return (
    <TextButtonWrapper
      icon="chapters"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClick}
      isLoading={isLoading}
      choiceText={finalCopy.generateChapters}
      hasUpsell={hasUpsell}
      isRootMenuButton={isRootMenuButton}
    />
  );
};

const Translations = ({
  isDisabled,
  isLoading,
  hasUpsell = false,
  onClick,
  isRootMenuButton,
}: Pick<Props, 'isDisabled' | 'onClick' | 'isLoading' | 'hasUpsell' | 'isRootMenuButton'>) => {
  const { sendEnterpriseCTAImpressionEvent } = useGetSvvManageBpEvents();
  useEffect(
    function handleUpsellStatus() {
      if (hasUpsell) {
        sendEnterpriseCTAImpressionEvent('ai_translate');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpsell],
  );

  return (
    <TextButtonWrapper
      icon="translations"
      isDisabled={isDisabled}
      onClick={onClick}
      isLoading={isLoading}
      choiceText={finalCopy.translateVideo}
      hasUpsell={hasUpsell}
      isRootMenuButton={isRootMenuButton}
    />
  );
};

const Highlights = ({
  isLoading,
  isPrimary,
  isDisabled,
  hasUpsell = false,
  onClick,
  isRootMenuButton,
}: Pick<
  Props,
  'isDisabled' | 'isPrimary' | 'hasUpsell' | 'onClick' | 'isLoading' | 'isRootMenuButton'
>) => {
  const { sendEnterpriseCTAImpressionEvent } = useGetSvvManageBpEvents();
  useEffect(
    function handleUpsellStatus() {
      if (hasUpsell) {
        sendEnterpriseCTAImpressionEvent('highlights');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpsell],
  );

  const isHighlightReel = shouldShowInDevelopmentFeature('highlight-reel', true);

  return (
    <TextButtonWrapper
      icon={isHighlightReel ? 'scene-media' : 'video'}
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={onClick}
      choiceText={isHighlightReel ? finalCopy.generateHighlightReel : finalCopy.generateHighlight}
      hasUpsell={hasUpsell}
      isRootMenuButton={isRootMenuButton}
    />
  );
};

const ActivateQnA = ({
  isDisabled,
  isPrimary,
  isLoading,
  title,
  hasUpsell = false,
  onClick,
  isRootMenuButton,
}: Pick<
  Props,
  'isDisabled' | 'isPrimary' | 'isLoading' | 'onClick' | 'title' | 'hasUpsell' | 'isRootMenuButton'
>) => {
  const { sendEnterpriseCTAImpressionEvent } = useGetSvvManageBpEvents();

  useEffect(
    function handleUpsellStatus() {
      if (hasUpsell) {
        sendEnterpriseCTAImpressionEvent('ask_ai');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasUpsell],
  );

  return (
    <TextButtonWrapper
      icon="question"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={onClick}
      choiceText={title || finalCopy.generateQnA}
      hasUpsell={hasUpsell}
      isRootMenuButton={isRootMenuButton}
    />
  );
};

const Save = ({
  isDisabled,
  isPrimary,
  isLoading,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick' | 'isLoading'>) => {
  return (
    <Choice
      icon="check"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={onClick}
    >
      {finalCopy.save}
    </Choice>
  );
};

const PlayMoment = ({
  children,
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'children' | 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  const inheritColors = useUIStore((state) => state.inheritColors);

  return (
    <Choice
      icon="play"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClick}
      inheritColors={inheritColors}
    >
      {children}
    </Choice>
  );
};

const Question = ({
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  return (
    <Choice icon="question" isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {finalCopy.askQuestionAction}
    </Choice>
  );
};

const EmailMe = ({
  isDisabled,
  isPrimary,
  isLoading,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'isLoading' | 'onClick'>) => {
  return (
    <Choice
      icon="email"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={onClick}
    >
      {finalCopy.emailMe}
    </Choice>
  );
};

const Empty = ({
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  return (
    <Choice icon="empty" isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {finalCopy.askQuestionAction}
    </Choice>
  );
};

const Replace = ({
  isDisabled,
  isPrimary,
  isLoading,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick' | 'isLoading'>) => {
  return (
    <Choice
      icon="check"
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      isLoading={isLoading}
      onClick={onClick}
    >
      {finalCopy.replace}
    </Choice>
  );
};

const SaveAsNewVideo = ({
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  return (
    <Choice icon="plus" isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {finalCopy.saveAsNewVideo}
    </Choice>
  );
};

const SavingAsNewVideo = () => {
  return (
    <>
      <Choice isLoading isDisabled={true}>
        {draftCopy.saving}
      </Choice>
    </>
  );
};

const OpenVideo = ({ onClick }: Pick<Props, 'onClick'>) => {
  return (
    <>
      <Choice icon="popOut" onClick={onClick}>
        {draftCopy.open}
      </Choice>
    </>
  );
};

const EditVideo = ({ onClick }: Pick<Props, 'onClick'>) => {
  return (
    <>
      <Choice icon="scissors" onClick={onClick}>
        {draftCopy.edit}
      </Choice>
    </>
  );
};

const SaveAndEditAsNewVideo = ({
  isDisabled,
  isPrimary,
  isLoading,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'isLoading' | 'onClick'>) => {
  return (
    <Choice
      icon="scissors"
      isLoading={isLoading}
      isDisabled={isDisabled}
      isPrimary={isPrimary}
      onClick={onClick}
    >
      {draftCopy.saveAndEditAsNewVideo}
    </Choice>
  );
};

const CopyLink = ({
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  return (
    <Choice icon="link" isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {finalCopy.copyLink}
    </Choice>
  );
};

const TranslateAndSave = ({
  children,
  isDisabled,
  isPrimary,
  onClick,
  dataId,
}: Pick<Props, 'children' | 'isDisabled' | 'isPrimary' | 'onClick' | 'dataId'>) => {
  return (
    <Choice dataId={dataId} isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {children}
    </Choice>
  );
};

const FinishAndSave = ({
  children,
  isDisabled,
  isPrimary,
  onClick,
}: Pick<Props, 'children' | 'isDisabled' | 'isPrimary' | 'onClick'>) => {
  return (
    <Choice isDisabled={isDisabled} isPrimary={isPrimary} onClick={onClick}>
      {children}
    </Choice>
  );
};

const AddCredits = ({
  children,
  isDisabled,
  onClick,
}: Pick<Props, 'children' | 'isDisabled' | 'onClick'>) => {
  return (
    <Choice icon="plus" isDisabled={isDisabled} isPrimary={false} onClick={onClick}>
      {children}
    </Choice>
  );
};

Choice.Summary = Summary;
Choice.Chapters = Chapters;
Choice.Translations = Translations;
Choice.Empty = Empty;
Choice.Highlights = Highlights;
Choice.Save = Save;
Choice.Question = Question;
Choice.ActivateQnA = ActivateQnA;
Choice.PlayMoment = PlayMoment;
Choice.EmailMe = EmailMe;
Choice.Replace = Replace;
Choice.SaveAsNewVideo = SaveAsNewVideo;
Choice.SaveAndEditAsNewVideo = SaveAndEditAsNewVideo;
Choice.SavingAsNewVideo = SavingAsNewVideo;
Choice.OpenVideo = OpenVideo;
Choice.EditVideo = EditVideo;
Choice.CopyLink = CopyLink;
Choice.TranslateAndSave = TranslateAndSave;
Choice.FinishAndSave = FinishAndSave;
Choice.AddCredits = AddCredits;

export default Choice;
